import type { EstadoGuia, Guia, OpcionPregunta } from '../tipos';

/**
 * Guía del remito nuevo (Remitos → "+ Nuevo Remito", también abierto con "Recibir" desde Pendientes de recibir).
 * Los textos se editan acá: cortos, de una o dos líneas, y con voseo como el resto de la app.
 */

/** Lo que la guía necesita saber del formulario (lo arma Remitos.tsx en cada render). */
export interface ContextoRemito {
  /** Ya hay una OC elegida (a mano o precargada con "Recibir"). */
  conOC: boolean;
  /** Sin OC y con un motivo cuya contraparte es un cliente (devolución). */
  deCliente: boolean;
  /** Sin OC y la mercadería suma al stock propio: cada ítem necesita su SKU. */
  pideSku: boolean;
  /** Hay al menos un ítem con descripción. */
  hayItems: boolean;
  /** Motivos de un remito sin OC que ofrece el backend (GET remitos/motivos). */
  motivos: { valor: string; etiqueta: string }[];
}

type Estado = EstadoGuia<ContextoRemito>;

/** La mercadería viene con OC: ya elegida, o el usuario respondió que sí. */
const vieneDeOC = ({ contexto, respuestas }: Estado) => contexto.conOC || respuestas.origen === 'si';

/** Aclaración de cada motivo en la pregunta (los que no están acá se muestran sólo con su nombre). */
const DETALLE_MOTIVO: Record<string, string> = {
  compra_sin_oc: 'Se compró sin orden de compra: se factura después.',
  bonificacion: 'El proveedor la regala o manda una muestra: entra a costo cero.',
  devolucion_cliente: 'Un cliente devuelve mercadería: vuelve al stock.',
  otro_ingreso: 'Cualquier otro ingreso de mercadería.',
  consignacion: 'Es del proveedor hasta que se venda: no suma al stock propio.',
  mercaderia_terceros: 'Es de otro: no suma al stock propio.',
};

const opcionesMotivo = ({ contexto }: Estado): OpcionPregunta[] =>
  contexto.motivos.map(m => ({
    valor: m.valor,
    etiqueta: m.etiqueta,
    detalle: DETALLE_MOTIVO[m.valor],
    acciones: [{ tipo: 'asignar', campo: 'motivo', valor: m.valor }],
  }));

export const guiaRemitoNuevo: Guia<ContextoRemito> = {
  id: 'remito-nuevo',
  nombre: 'Nuevo remito',
  pasos: [
    {
      tipo: 'pregunta',
      id: 'origen',
      cuando: ({ contexto }) => !contexto.conOC,
      pregunta: '¿La mercadería viene de una orden de compra?',
      opciones: [
        // Con OC la contraparte es el proveedor: si estaba elegida una devolución, se vuelve a compra.
        { valor: 'si', etiqueta: 'Sí, tiene OC', acciones: [{ tipo: 'asignar', campo: 'motivo', valor: 'compra_sin_oc' }] },
        { valor: 'no', etiqueta: 'No, entra sin OC' },
      ],
    },
    {
      tipo: 'pregunta',
      id: 'motivo',
      cuando: estado => !vieneDeOC(estado),
      pregunta: '¿Por qué entra la mercadería?',
      opciones: opcionesMotivo,
    },
    {
      tipo: 'campo',
      id: 'proveedor',
      objetivo: '[aria-label="Proveedor"]',
      cuando: ({ contexto }) => !contexto.deCliente,
      texto: estado => (vieneDeOC(estado)
        ? 'Buscá el proveedor que mandó la mercadería y elegilo de la lista: así aparecen sus OC pendientes.'
        : 'Buscá el proveedor que entrega la mercadería y elegilo de la lista.'),
    },
    {
      tipo: 'campo',
      id: 'oc',
      objetivo: '[aria-label="OC Recibible"]',
      cuando: vieneDeOC,
      texto: 'Elegí la OC que se está recibiendo: sus ítems pendientes se cargan solos.',
    },
    {
      tipo: 'campo',
      id: 'cliente',
      objetivo: '[aria-label="Cliente"]',
      cuando: ({ contexto }) => contexto.deCliente,
      texto: 'Buscá el cliente que devuelve la mercadería (por nombre o CUIT) y elegilo de la lista.',
    },
    {
      tipo: 'campo',
      id: 'numero',
      objetivo: '[aria-label="Nro Remito"]',
      texto: ({ contexto }) => (contexto.deCliente
        ? 'Si la devolución trae un comprobante, copiá su número. Si no, seguí.'
        : 'Copiá el número impreso en el remito de papel del proveedor: sirve para encontrarlo y cruzarlo con la factura.'),
    },
    {
      tipo: 'campo',
      id: 'fecha',
      objetivo: '[aria-label="Fecha"]',
      revisar: true,
      texto: 'Es el día en que llegó la mercadería. Viene con la de hoy: cambiala si llegó otro día.',
    },
    {
      tipo: 'campo',
      id: 'cantidades',
      objetivo: '[data-campo="Cantidad"]',
      cuando: ({ contexto }) => contexto.conOC,
      revisar: true,
      texto: 'Cant.Rec: contá lo que llegó. Si es menos de lo pedido, cargá lo que llegó; el resto queda pendiente en la OC.',
    },
    {
      tipo: 'campo',
      id: 'items',
      objetivo: '[data-campo="Descripción"]',
      cuando: ({ contexto }) => !contexto.conOC,
      completo: ({ contexto }) => contexto.hayItems,
      texto: 'Cargá cada ítem que llegó con la cantidad que contaste: buscalo abajo en "Buscar SKU del catálogo" o escribí la descripción.',
    },
    {
      tipo: 'campo',
      id: 'sku',
      objetivo: '[data-campo="SKU"]',
      cuando: ({ contexto }) => !contexto.conOC && contexto.pideSku,
      texto: 'Cada ítem necesita su SKU para sumar al stock: escribí 2 letras o más y elegilo de la lista.',
    },
    {
      tipo: 'campo',
      id: 'guardar',
      objetivo: '[data-ayuda="guardar"]',
      texto: 'Revisá los datos y tocá Guardar borrador: no suma al stock hasta que un administrador lo confirma. Si falta algo obligatorio, se marca en rojo.',
    },
  ],
};
